import { Icon } from '@iconify/react';
import React, { useMemo } from 'react';
import {
  Table as ShadcnTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/shadcn/table';
import { cn } from '@/lib/utils';

export type Column = {
  key: string;
  title: string;
  width?: string;
  align?: 'left' | 'center' | 'right';
  headerAlign?: 'left' | 'center' | 'right';
  sortable?: boolean;
};

type Props = {
  columns: Column[];
  data: any[];
  renderCell?: (
    row: any,
    columnKey: string,
    align?: 'left' | 'center' | 'right',
    rowIndex?: number
  ) => React.ReactNode;
  renderActions?: (row: any) => React.ReactNode;
  isLoading?: boolean;
  loadingRows?: number;
  noCardView?: boolean;
  onSort?: (key: string, order: 'asc' | 'desc') => void;
  sortKey?: string;
  sortOrder?: 'asc' | 'desc';
  sortingMode?: 'local' | 'remote';
  className?: string;
};

const Table = ({
  columns,
  data,
  renderCell,
  renderActions,
  isLoading = false,
  loadingRows = 5,
  noCardView = false,
  onSort,
  sortKey,
  sortOrder,
  sortingMode = 'remote',
  className,
}: Props) => {
  // Internal selection for local sorting if needed
  const displayData = useMemo(() => {
    if (sortingMode === 'remote' || !sortKey || !sortOrder) {
      return data;
    }

    return [...(data || [])].sort((a, b) => {
      const aValue = a[sortKey];
      const bValue = b[sortKey];

      if (aValue === bValue) return 0;
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      const result = aValue < bValue ? -1 : 1;
      return sortOrder === 'asc' ? result : -result;
    });
  }, [data, sortKey, sortOrder, sortingMode]);

  const handleSort = (key: string) => {
    if (!onSort) return;

    let nextOrder: 'asc' | 'desc' = 'asc';
    if (sortKey === key) {
      nextOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    }

    onSort(key, nextOrder);
  };

  return (
    <div className={cn('', className)}>
      {/* Mobile Card View */}
      {!noCardView && (
        <div className="md:hidden space-y-4">
          {isLoading
            ? Array.from({ length: loadingRows }).map((_, idx) => (
                <div key={idx} className="bg-white p-4 rounded-lg shadow animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                </div>
              ))
            : displayData?.map((row, idx) => (
                <div key={idx} className="bg-white p-4 rounded-lg border shadow-sm relative">
                  {renderActions && (
                    <div className="absolute top-4 right-4">{renderActions(row)}</div>
                  )}
                  <div className="space-y-2 pr-8">
                    {columns.map((col) => (
                      <div
                        key={col.key}
                        className="flex justify-between flex-col border-b last:border-0 pb-2 last:pb-0"
                      >
                        <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">
                          {col.title}
                        </span>
                        <span className="text-sm text-gray-900 mt-1">
                          {renderCell ? renderCell(row, col.key, col.align, idx) : row[col.key]}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
        </div>
      )}

      {/* Desktop Table View */}
      <div
        className={cn(
          'rounded-md border bg-white overflow-x-auto max-w-full',
          !noCardView && 'hidden md:block'
        )}
      >
        <ShadcnTable>
          <TableHeader>
            <TableRow className="bg-gc-50 hover:bg-gc-50">
              {columns.map((col) => (
                <TableHead
                  key={col.key}
                  className={cn(
                    'py-3 px-4 text-sm font-semibold text-gc-100',
                    col.headerAlign === 'center' && 'text-center',
                    col.headerAlign === 'right' && 'text-right',
                    col.width,
                    col.sortable && 'cursor-pointer select-none hover:bg-gc-20 transition-colors'
                  )}
                  onClick={() => col.sortable && handleSort(col.key)}
                >
                  <div
                    className={cn(
                      'flex items-center gap-2',
                      col.headerAlign === 'center'
                        ? 'justify-center'
                        : col.headerAlign === 'right'
                          ? 'justify-end'
                          : ''
                    )}
                  >
                    <span>{col.title}</span>
                    {col.sortable && (
                      <span className="text-gc-100">
                        {sortKey === col.key ? (
                          sortOrder === 'asc' ? (
                            <Icon icon="lucide:chevron-up" className="w-4 h-4" />
                          ) : (
                            <Icon icon="lucide:chevron-down" className="w-4 h-4" />
                          )
                        ) : (
                          <Icon icon="lucide:chevrons-up-down" className="w-4 h-4 opacity-30" />
                        )}
                      </span>
                    )}
                  </div>
                </TableHead>
              ))}
              {renderActions && <TableHead className="py-3 px-4 text-right w-20"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: loadingRows }).map((_, rIdx) => (
                <TableRow key={`skeleton-${rIdx}`}>
                  {columns.map((col) => (
                    <TableCell
                      key={`${col.key}-s-${rIdx}`}
                      className={cn(
                        'py-4 px-4 align-top',
                        col.align === 'center'
                          ? 'text-center'
                          : col.align === 'right'
                            ? 'text-right'
                            : ''
                      )}
                    >
                      <div
                        className={cn(
                          'h-4 bg-gc-20 rounded-md animate-pulse',
                          col.width ? '' : 'w-3/4'
                        )}
                      />
                    </TableCell>
                  ))}
                  {renderActions && (
                    <TableCell className="py-4 px-4 text-right">
                      <div className="h-4 bg-gc-20 rounded-md w-8 animate-pulse ml-auto" />
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : displayData?.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (renderActions ? 1 : 0)}
                  className="h-24 text-center text-muted-foreground"
                >
                  No data available.
                </TableCell>
              </TableRow>
            ) : (
              displayData?.map((row, idx) => (
                <TableRow key={idx} className="hover:bg-gray-50/50">
                  {columns.map((col) => (
                    <TableCell
                      key={col.key}
                      className={cn(
                        'py-4 px-4 align-top text-sm text-[#0E1726]',
                        col.align === 'center'
                          ? 'text-center'
                          : col.align === 'right'
                            ? 'text-right'
                            : ''
                      )}
                    >
                      {renderCell ? renderCell(row, col.key, col.align, idx) : row[col.key]}
                    </TableCell>
                  ))}
                  {renderActions && (
                    <TableCell className="py-4 px-4 text-right">{renderActions(row)}</TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </ShadcnTable>
      </div>
    </div>
  );
};

export default Table;
